// web-commands.ts — the PER-APP half of the web shape: maps study_companion's Tauri data
// commands (normally served by ipc.rs/db.rs over native SQLite) to the shared browser WebStore.
// Plain CRUD is a few lines each; the subject counts native computes on read are re-derived here.
// The photo/OCR commands are native-only (Apple Vision + on-device file store); on web they
// degrade to empty results so the UI's own fallbacks ("paste it instead") take over.

import type { CommandHandler, WebStore } from "./web-runtime";
import { browserSearch, browserFetch } from "./web-search-browser";

const SUBJECTS = "subjects";
const MATERIALS = "materials";
const ARTIFACTS = "artifacts";
const SESSIONS = "study_sessions";

function uuid(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
function nowIso(): string {
  return new Date().toISOString();
}
function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
}

/** Normalize a topic tag — lowercase, trim, collapse internal whitespace (mirrors db.rs::normalize). */
function normalizeTag(raw: string): string {
  return String(raw).trim().toLowerCase().split(/\s+/).filter(Boolean).join(" ");
}

function normalizeSource(s: unknown): string {
  const v = String(s ?? "typed");
  return v === "typed" || v === "photo" ? v : "typed";
}

function normalizeKind(s: unknown): string | null {
  const v = String(s ?? "");
  return v === "notes" || v === "explanation" || v === "plan" ? v : null;
}

type Row = Record<string, unknown> & { id: string };

export function makeWebCommands(db: WebStore): Record<string, CommandHandler> {
  // Re-derive the computed subject counts native attaches on read (db.rs::hydrate_subject).
  const hydrateSubject = (s: Row): Row => {
    const sid = String(s.id);
    const sess = db.all(SESSIONS).filter((r) => (r as Row).subject_id === sid) as Row[];
    const last = sess.map((r) => String(r.studied_on ?? "")).sort().pop() ?? null;
    return {
      ...s,
      material_count: db.all(MATERIALS).filter((r) => (r as Row).subject_id === sid).length,
      artifact_count: db.all(ARTIFACTS).filter((r) => (r as Row).subject_id === sid).length,
      session_count: sess.length,
      total_minutes: sess.reduce((n, r) => n + (Number(r.minutes) || 0), 0),
      last_studied: last || null,
    };
  };
  const byName = (a: unknown, b: unknown) =>
    String((a as Row).name ?? "").localeCompare(String((b as Row).name ?? ""), undefined, { sensitivity: "base" });
  const newestFirst = (a: unknown, b: unknown) =>
    String((b as Row).created_at ?? "").localeCompare(String((a as Row).created_at ?? ""));

  return {

    // ── WEB-SURFACE RETRIEVAL (Pattern 7, web half) ─────────────────────────────
    // On native, `web_search`/`web_fetch` are Rust commands browsing on the DEVICE. In a browser
    // there is no Rust, and without these the chat's `maybeWebContext` silently returns nothing —
    // the customer gets an ungrounded answer where the phone would have cited sources.
    // Here the BROWSER does it, on the customer's own IP: Tier 1 sources it may read directly
    // (no middleman), then a keyless reader for the open web. Oriora runs no search.
    web_search: async (args: Record<string, unknown>) =>
      await browserSearch(String(args.query ?? ""), typeof args.limit === "number" ? args.limit : 5),
    web_fetch: async (args: Record<string, unknown>) =>
      await browserFetch(String(args.url ?? ""), typeof args.maxChars === "number" ? args.maxChars : 6000),

    // ── Subjects ───────────────────────────────────────────────────────────────
    list_subjects: () => (db.all(SUBJECTS) as Row[]).sort(byName).map(hydrateSubject),
    create_subject: ({ name }) => {
      const row = { id: uuid(), name: String(name ?? "").trim(), created_at: nowIso(), updated_at: nowIso() };
      db.insert(SUBJECTS, row);
      return hydrateSubject(row);
    },
    rename_subject: ({ id, name }) => {
      db.update(SUBJECTS, String(id), { name: String(name ?? "").trim(), updated_at: nowIso() });
      return null;
    },
    delete_subject: ({ id }) => {
      const sid = String(id);
      db.remove(SUBJECTS, sid);
      // ON DELETE CASCADE on native clears materials + artifacts + sessions; mirror that here.
      for (const t of [MATERIALS, ARTIFACTS, SESSIONS]) {
        for (const r of db.all(t)) {
          if ((r as Row).subject_id === sid) db.remove(t, r.id);
        }
      }
      return null;
    },

    // ── Materials ──────────────────────────────────────────────────────────────
    list_materials: ({ subjectId }) => {
      const rows = (db.all(MATERIALS) as Row[]).sort(newestFirst);
      return subjectId ? rows.filter((r) => r.subject_id === String(subjectId)) : rows;
    },
    get_material: ({ id }) => db.get(MATERIALS, String(id)),
    create_material: ({ input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      const row = {
        id: uuid(),
        subject_id: String(i.subject_id ?? ""),
        topic: normalizeTag(String(i.topic ?? "")),
        title: String(i.title ?? "").trim(),
        source: normalizeSource(i.source),
        content: String(i.content ?? "").trim(),
        created_at: nowIso(),
        updated_at: nowIso(),
      };
      return db.insert(MATERIALS, row);
    },
    delete_material: ({ id }) => {
      db.remove(MATERIALS, String(id));
      return null;
    },

    // ── Artifacts ──────────────────────────────────────────────────────────────
    list_artifacts: ({ subjectId, kind }) => {
      let rows = (db.all(ARTIFACTS) as Row[]).sort(newestFirst);
      if (subjectId) rows = rows.filter((r) => r.subject_id === String(subjectId));
      const k = normalizeKind(kind);
      if (kind && k) rows = rows.filter((r) => r.kind === k);
      return rows;
    },
    create_artifact: ({ input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      const kind = normalizeKind(i.kind);
      if (!kind) throw new Error("bad_kind");
      const row = {
        id: uuid(),
        subject_id: String(i.subject_id ?? ""),
        material_id: String(i.material_id ?? ""),
        kind,
        title: String(i.title ?? "").trim(),
        body: String(i.body ?? ""),
        created_at: nowIso(),
      };
      return db.insert(ARTIFACTS, row);
    },
    delete_artifact: ({ id }) => {
      db.remove(ARTIFACTS, String(id));
      return null;
    },

    // ── Study sessions ─────────────────────────────────────────────────────────
    add_session: ({ input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      const row = {
        id: uuid(),
        subject_id: String(i.subject_id ?? ""),
        studied_on: String(i.studied_on ?? todayLocal()),
        minutes: Math.max(1, Math.min(1440, Math.round(Number(i.minutes) || 1))),
        note: String(i.note ?? "").trim(),
        created_at: nowIso(),
      };
      return db.insert(SESSIONS, row);
    },
    list_sessions: ({ subjectId, limit }) => {
      let rows = (db.all(SESSIONS) as Row[]).sort((a, b) => {
        const ao = String(a.studied_on ?? "");
        const bo = String(b.studied_on ?? "");
        if (ao !== bo) return bo.localeCompare(ao);
        return String(b.created_at ?? "").localeCompare(String(a.created_at ?? ""));
      });
      if (subjectId) rows = rows.filter((r) => r.subject_id === String(subjectId));
      const n = Number(limit);
      return Number.isFinite(n) && n > 0 ? rows.slice(0, n) : rows;
    },
    delete_session: ({ id }) => {
      db.remove(SESSIONS, String(id));
      return null;
    },

    // ── Native-only (Apple Vision OCR + on-device photo store). Web has no on-device file
    //    store or OCR engine, so these return empty and the Digest sheet's own fallbacks
    //    ("paste it instead") take over.
    save_page_photo: () => "",
    ocr_image: () => "",
  };
}
